import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  InvoiceBulkCategoryModal,
  InvoiceCategoryModal,
} from "@/components/invoices/InvoiceCategoryModal";
import { InvoiceDataTable } from "@/components/invoices/InvoiceDataTable";
import { InvoiceOverview } from "@/components/invoices/InvoiceOverview";
import {
  InvoiceBulkStatusModal,
  InvoiceStatusModal,
} from "@/components/invoices/InvoiceStatusModal";
import { InvoiceFilterOffcanvas } from "@/components/invoices/InvoiceFilterOffcanvas";
import { InvoiceSelectionBar } from "@/components/invoices/InvoiceSelectionBar";
import { InvoiceSelectionSummary } from "@/components/invoices/InvoiceSelectionSummary";
import { InvoiceTableToolbar } from "@/components/invoices/InvoiceTableToolbar";
import { ActiveFilterChips } from "@/components/common/ActiveFilterChips";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useErrorToast } from "@/hooks/use-error-toast";
import { useAssignees } from "@/features/invoices/useAssignees";
import { useInvoiceList } from "@/features/invoices/useInvoiceList";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import { selectCurrentUser } from "@/features/auth/authSlice";
import {
  cloneInvoiceFilters,
  countActiveInvoiceFilters,
  invoiceFiltersConflict,
  mergeInvoiceFilters,
} from "@/lib/invoiceFilters";
import { getFailureTypeLabel } from "@/lib/failedInvoiceTypes";
import { getInvoiceCategory } from "@/lib/invoiceCategories";
import { getOverviewStats } from "@/lib/invoiceOverviewStats";
import { pickOverviewInsights } from "@/lib/overviewInsights";
import { describeInvoiceFilters } from "@/lib/activeFilters";
import { formatCurrency, formatInvoiceCount } from "@/lib/invoiceStatus";
import {
  INVOICE_PAGE_VARIANTS,
  QUEUE_CHIP_FILTERS,
  queueChipForStatuses,
  SORT_DIRECTIONS,
} from "@/lib/invoicePageConfig";
import { readPersisted, writePersisted } from "@/lib/persistedState";
import { pluralizeInvoices, runQueue, summarizeQueue } from "@/lib/taskQueue";
import { fetchInvoiceFile, invoiceFileName, saveBlob } from "@/lib/invoiceFile";
import {
  selectTablePrefs,
  setPageNumber as setStoredPageNumber,
  setPageSize as setStoredPageSize,
  setSorting,
} from "@/features/tables/tablePrefsSlice";
import {
  useAssignInvoiceMutation,
  useDeleteInvoiceMutation,
  useUpdateInvoiceCategoryMutation,
  useUpdateInvoiceStatusMutation,
  useGetInvoiceInsightsQuery,
  useGetMyInvoiceInsightsQuery,
} from "@/services/invoices/invoicesApi";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [8, 16, 24, 50];

/** Per-queue filter state survives navigation, but is not shared between queues. */
const filtersKey = (variant) => `filters.${variant}`;
const searchKey = (variant) => `search.${variant}`;
const categoryKey = (variant) => `category.${variant}`;
const failureTypeKey = (variant) => `failureType.${variant}`;

/**
 * How the delete dialog names an invoice. Either the number or the supplier can
 * be missing on an extracted invoice, so each part is optional.
 */
function describeInvoice({ invoiceNumber, companyName }) {
  if (invoiceNumber) return companyName ? `${invoiceNumber} from ${companyName}` : invoiceNumber;
  return companyName ? `The invoice from ${companyName}` : "This invoice";
}

/**
 * "Page [n] / total": type a number and press Enter (or leave the field) to jump.
 * The draft is local so typing does not fire a request per keystroke; the parent
 * re-keys it on the real page, so it snaps back after a jump or a clamp.
 */
function PageJump({ page, totalPages, onJump }) {
  const [draft, setDraft] = useState(String(page));

  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isFinite(parsed)) {
      setDraft(String(page));
      return;
    }
    const next = Math.min(Math.max(parsed, 1), totalPages);
    setDraft(String(next));
    if (next !== page) onJump(next);
  };

  return (
    <label className="me-2 flex items-center gap-1.5 text-sm">
      <span>Page</span>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        max={totalPages}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
        aria-label={`Go to page, 1 to ${totalPages}`}
        className="num h-8 w-14 rounded-lg border-0 bg-muted px-2 text-center text-sm text-foreground [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="num">of {totalPages}</span>
    </label>
  );
}

export function InvoicesPage({ variant }) {
  const config = INVOICE_PAGE_VARIANTS[variant];
  const location = useLocation();
  const dispatch = useDispatch();
  const can = useHasPermission();
  const currentUser = useSelector(selectCurrentUser);
  const showErrorToast = useErrorToast();

  // Sort choice is per queue and survives opening an invoice and coming back,
  // which is the whole reason it lives in the store rather than in this page.
  // The page and page size live there too, so returning from an invoice lands
  // on the page the reader left rather than the first one.
  const { sortBy, sortDirection, pageNumber: storedPage, pageSize: storedSize } = useSelector(
    selectTablePrefs(variant),
  );
  // Guard against a missing or nonsensical stored value so the query never
  // asks for page NaN or size 0.
  const pageNumber = Number.isInteger(storedPage) && storedPage >= 1 ? storedPage : 1;
  const pageSize = PAGE_SIZE_OPTIONS.includes(storedSize) ? storedSize : 16;
  const setPageNumber = useCallback(
    (page) => dispatch(setStoredPageNumber({ key: variant, pageNumber: page })),
    [dispatch, variant],
  );
  const setPageSize = useCallback(
    (size) => dispatch(setStoredPageSize({ key: variant, pageSize: size })),
    [dispatch, variant],
  );

  /* ------------------------------------------------------------ view state */

  // The coloured toolbar filters persist alongside search and the advanced
  // panel; the old app kept these in localStorage too, and losing the failure
  // type on every navigation back into the queue is what made it worth keeping.
  const [categoryFilter, setCategoryFilter] = useState(() =>
    readPersisted(categoryKey(variant), ""),
  );
  const [failureTypeFilter, setFailureTypeFilter] = useState(() =>
    readPersisted(failureTypeKey(variant), ""),
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkWorking, setIsBulkWorking] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [categoryTarget, setCategoryTarget] = useState(null);

  const [searchValue, setSearchValue] = useState(() => readPersisted(searchKey(variant), ""));
  const [advancedFilters, setAdvancedFilters] = useState(() =>
    cloneInvoiceFilters(readPersisted(filtersKey(variant))),
  );

  // The Under Review / Failed / Successful chips and tiles are shortcuts for the
  // panel's Processing status field, so they are not state of their own: the
  // chip is read back from the panel's filter, and picking one writes it there.
  const queueChip = queueChipForStatuses(advancedFilters.processingStatusIds);

  const debouncedSearch = useDebouncedValue(searchValue.trim(), 350);

  useEffect(() => {
    writePersisted(searchKey(variant), searchValue);
  }, [variant, searchValue]);

  /* ------------------------------------------------------------- the query */

  // Filters compose: the queue's own constraint and the advanced filter panel,
  // which already holds the status the chips picked.
  const filters = useMemo(
    () => mergeInvoiceFilters(config.baseFilters, advancedFilters),
    [config.baseFilters, advancedFilters],
  );

  const queryArgs = useMemo(
    () => ({
      page: pageNumber,
      pageSize,
      query: debouncedSearch || undefined,
      categoryId: categoryFilter || undefined,
      failureTypes: failureTypeFilter ? [failureTypeFilter] : undefined,
      sortBy: sortBy || undefined,
      // The store keeps asc/desc; the API speaks Syncfusion's Ascending/Descending.
      sortDirection: sortDirection ? SORT_DIRECTIONS[sortDirection] : undefined,
      filters,
      currentUserGuid: currentUser?.guid,
    }),
    [
      pageNumber,
      pageSize,
      debouncedSearch,
      categoryFilter,
      failureTypeFilter,
      sortBy,
      sortDirection,
      filters,
      currentUser?.guid,
    ],
  );

  const { invoices, pagination, isLoading, isFetching, isSuccess, error } = useInvoiceList(
    config.source,
    queryArgs,
    { skip: invoiceFiltersConflict(filters) },
  );

  useEffect(() => {
    if (error) showErrorToast(error);
  }, [error, showErrorToast]);

  /* ------------------------------------------------------------- insights */

  const isMyQueue = config.source === "my";
  const overviewStats = getOverviewStats(config);

  // The strip is asked with the same filters as the list — the queue's own
  // constraint, the search, the category and failure-type chips and the filter
  // panel — so its counts follow them. The endpoint leaves each count's own
  // filter out, which is what makes a tile's number the rows its click lists.
  const insightArgs = useMemo(
    () => ({
      query: debouncedSearch || undefined,
      categoryId: categoryFilter || undefined,
      failureTypes: failureTypeFilter ? [failureTypeFilter] : undefined,
      filters,
    }),
    [debouncedSearch, categoryFilter, failureTypeFilter, filters],
  );
  // Like the list, it is not asked when the filters contradict each other (a
  // status the queue excludes), since the list then shows nothing.
  const filtersConflict = invoiceFiltersConflict(filters);
  const workspaceInsights = useGetInvoiceInsightsQuery(insightArgs, {
    skip: !config.showStats || isMyQueue || filtersConflict,
  });
  const myInsights = useGetMyInvoiceInsightsQuery(insightArgs, {
    skip: !config.showStats || !isMyQueue || filtersConflict,
  });
  const insightsQuery = isMyQueue ? myInsights : workspaceInsights;

  // Undefined until the first answer, so the strip shows its skeleton; after
  // that the previous counts stay up while a new filter is being counted. After
  // a failed request only the answer for the current filters is kept, so the
  // tiles read as dashes rather than the previous filters' counts.
  const overviewInsights = pickOverviewInsights(insightsQuery);

  // A failed request leaves the tiles as dashes; say why.
  const insightsError = insightsQuery.error;
  useEffect(() => {
    if (insightsError) showErrorToast(insightsError);
  }, [insightsError, showErrorToast]);

  /* ------------------------------------------------------------ assignment */

  // Assigning is open to everyone who can see the invoice: whoever holds it has
  // to be able to hand it on when they are done. Status and category changes
  // still need the write grant.
  const canUpdate = can(PERMISSIONS.UPDATE_INVOICE);
  // Reclassifying needs both the write grant and the category list to choose from.
  const canRecategorise = canUpdate && can(PERMISSIONS.INVOICE_CATEGORIES);
  // Downloading the supplier document is its own grant in the auth service.
  const canDownload = can(PERMISSIONS.DOWNLOAD_ATTACHMENT);
  const canDelete = can(PERMISSIONS.DELETE_INVOICE);
  // Loaded on first use of an assignee control; eagerly only when an assignee
  // filter is applied, because its chip has to resolve the id to a name.
  const { users: allUsers, request: requestAssignees } = useAssignees({
    eager: Boolean(advancedFilters.assignedToId),
  });

  // On My Invoices every row is already assigned to the viewer, so offering
  // themselves as an assignee is a no-op. The old app applied the same rule.
  const users = useMemo(
    () =>
      variant === "my-invoices"
        ? allUsers.filter((user) => user.guid !== currentUser?.guid)
        : allUsers,
    [allUsers, currentUser?.guid, variant],
  );

  const [assignInvoice] = useAssignInvoiceMutation();
  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();
  const [updateInvoiceStatus] = useUpdateInvoiceStatusMutation();
  const [updateInvoiceCategory] = useUpdateInvoiceCategoryMutation();

  const handleAssigneeChange = useCallback(
    async (invoice, userGuid) => {
      try {
        await assignInvoice({ guid: invoice.id, userGuid }).unwrap();
        const label = invoice.invoiceNumber || "Invoice";
        const assigneeName = allUsers.find((user) => user.guid === userGuid)?.name;
        toast.success(
          userGuid
            ? `${label} assigned${assigneeName ? ` to ${assigneeName}` : ""}`
            : `${label} unassigned`,
        );
      } catch (assignError) {
        showErrorToast(assignError);
      }
    },
    [allUsers, assignInvoice, showErrorToast],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteInvoice(pendingDelete.id).unwrap();
      toast.success(`Deleted ${pendingDelete.invoiceNumber || "invoice"}`);
      setSelectedIds((current) => {
        if (!current.has(pendingDelete.id)) return current;
        const next = new Set(current);
        next.delete(pendingDelete.id);
        return next;
      });
    } catch (deleteError) {
      showErrorToast(deleteError);
    } finally {
      setPendingDelete(null);
    }
  }, [deleteInvoice, pendingDelete, showErrorToast]);

  /* ------------------------------------------------------------- selection */

  // Selection only ever means "rows visible right now". Deriving the
  // intersection avoids an effect that rewrites state after every fetch, and
  // means a row filtered out of the result set cannot be acted on invisibly.
  const visibleSelectedIds = useMemo(() => {
    if (selectedIds.size === 0) return selectedIds;
    return new Set(invoices.filter((invoice) => selectedIds.has(invoice.id)).map((i) => i.id));
  }, [invoices, selectedIds]);

  // Selected invoices can span currencies, so each amount is summed per symbol
  // rather than blindly added together, then rendered as one string per
  // currency the selection actually contains.
  const selectedTotals = useMemo(() => {
    if (visibleSelectedIds.size === 0) return { net: null, total: null };

    const netByCurrency = new Map();
    const totalByCurrency = new Map();

    for (const invoice of invoices) {
      if (!visibleSelectedIds.has(invoice.id)) continue;
      const symbol = invoice.currencySymbol ?? "£";

      const netAmount = Number.parseFloat(invoice.netAmount);
      if (Number.isFinite(netAmount)) {
        netByCurrency.set(symbol, (netByCurrency.get(symbol) ?? 0) + netAmount);
      }

      const totalAmount = Number.parseFloat(invoice.totalAmount);
      if (Number.isFinite(totalAmount)) {
        totalByCurrency.set(symbol, (totalByCurrency.get(symbol) ?? 0) + totalAmount);
      }
    }

    const format = (byCurrency) =>
      byCurrency.size === 0
        ? null
        : Array.from(byCurrency, ([symbol, sum]) => formatCurrency(sum, symbol)).join(" + ");

    return { net: format(netByCurrency), total: format(totalByCurrency) };
  }, [invoices, visibleSelectedIds]);

  /* ----------------------------------------------------------- bulk actions */

  /**
   * Every bulk action is N single-invoice requests, because no endpoint accepts
   * more than one guid. `runQueue` caps how many are in flight instead of
   * firing the whole selection at once, and never rejects — one failure must
   * not abandon the rest. Cache tags handle the refresh; the old app also
   * removed rows by hand and then forced a refetch on top of invalidation.
   */
  const runBulk = useCallback(
    async (ids, action, { verb, pastTense, concurrency = 4 }) => {
      setIsBulkWorking(true);
      setBulkProgress({ done: 0, total: ids.length, label: verb });

      try {
        const results = await runQueue(ids, action, {
          concurrency,
          onProgress: (done, total) => setBulkProgress({ done, total, label: verb }),
        });
        const { succeeded, failed } = summarizeQueue(results);

        if (succeeded > 0) toast.success(`${pluralizeInvoices(succeeded)} ${pastTense}`);
        if (failed > 0) toast.error(`Could not ${verb} ${pluralizeInvoices(failed)}`);
        if (succeeded > 0) setSelectedIds(new Set());

        return { succeeded, failed };
      } finally {
        setIsBulkWorking(false);
        setBulkProgress(null);
      }
    },
    [],
  );

  const handleBulkAssign = useCallback(
    (userGuid) =>
      runBulk(
        [...visibleSelectedIds],
        (guid) => assignInvoice({ guid, userGuid }).unwrap(),
        { verb: "assign", pastTense: userGuid ? "assigned" : "unassigned" },
      ),
    [assignInvoice, runBulk, visibleSelectedIds],
  );

  const handleBulkDelete = useCallback(async () => {
    await runBulk([...visibleSelectedIds], (guid) => deleteInvoice(guid).unwrap(), {
      verb: "delete",
      pastTense: "deleted",
    });
    setBulkDeleteOpen(false);
  }, [deleteInvoice, runBulk, visibleSelectedIds]);

  const handleBulkStatus = useCallback(
    async (processingStatusId) => {
      await runBulk(
        [...visibleSelectedIds],
        (guid) => updateInvoiceStatus({ guid, processingStatusId }).unwrap(),
        { verb: "update", pastTense: "updated" },
      );
      setBulkStatusOpen(false);
    },
    [runBulk, updateInvoiceStatus, visibleSelectedIds],
  );

  const handleBulkCategory = useCallback(
    async (categoryGuid) => {
      await runBulk(
        [...visibleSelectedIds],
        (guid) => updateInvoiceCategory({ guid, categoryGuid }).unwrap(),
        { verb: "update", pastTense: "updated" },
      );
      setBulkCategoryOpen(false);
    },
    [runBulk, updateInvoiceCategory, visibleSelectedIds],
  );

  /**
   * Downloads run at concurrency 2: these are whole PDFs rather than small JSON
   * calls, and the saves are triggered one at a time as each arrives — browsers
   * treat a burst of programmatic downloads as a popup and block all but the
   * first.
   */
  const handleBulkDownload = useCallback(async () => {
    const selected = invoices.filter((invoice) => visibleSelectedIds.has(invoice.id));

    await runBulk(
      selected,
      async (invoice) => {
        const blob = await fetchInvoiceFile(invoice.id);
        saveBlob(blob, invoiceFileName(invoice));
      },
      { verb: "download", pastTense: "downloaded", concurrency: 2 },
    );
  }, [invoices, runBulk, visibleSelectedIds]);

  /* ------------------------------------------------------------ pagination */

  const totalPages = Math.max(1, pagination.totalPages || 1);
  // Clamped on read for display; the effect below corrects the stored page
  // (which is what the request uses) once a real result says how many exist.
  const currentPage = Math.min(pageNumber, totalPages);

  // The request itself uses the stored page, and that page now outlives the
  // visit: coming back after the queue shrank (an invoice reassigned or deleted)
  // would ask for a page past the end and show it empty. Once a real result says
  // how many pages exist, pull the stored page back inside them.
  const knownTotalPages = pagination.totalPages || 0;
  useEffect(() => {
    // A 400/404 for a page past the end would otherwise leave the stored page
    // out of range; other errors (expired session, throttling, network) keep it.
    if (!isFetching && pageNumber > 1 && (error?.status === 400 || error?.status === 404)) {
      setPageNumber(1);
      return;
    }
    if (isFetching || error || !isSuccess) return;
    // An emptied queue reports zero pages; page 1 is the only page that exists.
    const lastPage = Math.max(1, knownTotalPages);
    if (pageNumber > lastPage) setPageNumber(lastPage);
  }, [isFetching, isSuccess, error, knownTotalPages, pageNumber, setPageNumber]);

  const resetToFirstPage = () => setPageNumber(1);

  // Clicking the active column a third time clears the sort rather than cycling
  // asc/desc forever, so there is always a way back to the server's default.
  const handleSortChange = (field, direction) => {
    const clearing = sortBy === field && sortDirection === "desc";
    dispatch(
      setSorting({
        key: variant,
        sortBy: clearing ? null : field,
        sortDirection: clearing ? null : direction,
      }),
    );
    resetToFirstPage();
  };

  const toggleRow = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((current) => {
      const pageIds = invoices.map((invoice) => invoice.id);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => current.has(id));
      const next = new Set(current);
      pageIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const selectedCount = visibleSelectedIds.size;

  /* ------------------------------------------------------------------ view */

  const applyFilters = (next) => {
    setAdvancedFilters(next);
    writePersisted(filtersKey(variant), next);
    resetToFirstPage();
  };

  // Shared by the toolbar's queue-chip bar and the overview strip's Under
  // Review / Failed / Successful tiles. A chip replaces whatever statuses the
  // panel had; "all" empties the field.
  const handleQueueChipChange = (value) => {
    applyFilters(
      cloneInvoiceFilters({
        ...advancedFilters,
        processingStatusIds: [...(QUEUE_CHIP_FILTERS[value]?.processingStatusIds ?? [])],
      }),
    );
  };

  // Shared by the toolbar's own chips and the overview strip's Samples /
  // Credit Notes / Foreign Exchange tiles, so either control narrows the
  // same query param the same way.
  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    writePersisted(categoryKey(variant), value);
    resetToFirstPage();
  };

  const handleFailureTypeFilterChange = (value) => {
    setFailureTypeFilter(value);
    writePersisted(failureTypeKey(variant), value);
    resetToFirstPage();
  };

  // What the panel currently has applied, spelled out. The old app printed the
  // same readout on every queue; here each chip also clears the filter it names.
  // The Category and Failure type buttons and the search narrow the list the same
  // way, so they get a chip of their own here; the status buttons already show up as the
  // panel's Processing status chip.
  const buttonFilterChips = [
    categoryFilter !== "" && {
      key: "button-category",
      label: "Category",
      value: getInvoiceCategory(categoryFilter)?.name ?? String(categoryFilter),
      clear: () => handleCategoryFilterChange(""),
    },
    failureTypeFilter !== "" && {
      key: "button-failureType",
      label: "Failure type",
      value: getFailureTypeLabel(failureTypeFilter),
      clear: () => handleFailureTypeFilterChange(""),
    },
    debouncedSearch && {
      key: "button-search",
      label: "Search",
      value: debouncedSearch,
      clear: () => {
        setSearchValue("");
        resetToFirstPage();
      },
    },
  ].filter(Boolean);
  const activeFilterChips = [
    ...describeInvoiceFilters(advancedFilters, { users: allUsers }),
    ...buttonFilterChips,
  ];

  const removeFilterChip = (chip) =>
    chip.clear
      ? chip.clear()
      : applyFilters(cloneInvoiceFilters({ ...advancedFilters, ...chip.patch }));

  const clearAllFilters = () => applyFilters(cloneInvoiceFilters());

  // Clear, at the end of the applied-filter row, resets every narrowing control:
  // the search, the buttons and the filter panel.
  const clearToolbar = () => {
    setSearchValue("");
    handleCategoryFilterChange("");
    handleFailureTypeFilterChange("");
    clearAllFilters();
  };

  return (
    <div className="content-stack flex h-full min-h-0 flex-1 flex-col">
      {config.showStats && (
        <InvoiceOverview
          stats={overviewStats}
          insights={overviewInsights}
          isLoading={insightsQuery.isLoading}
          selectedChip={queueChip}
          onSelectChip={handleQueueChipChange}
          selectedCategory={categoryFilter}
          onSelectCategory={handleCategoryFilterChange}
          selectedFailureType={failureTypeFilter}
          onSelectFailureType={handleFailureTypeFilterChange}
        />
      )}

      <section className={cn("page-panel relative", selectedCount > 0 && "pb-2")}>
        <InvoiceTableToolbar
          config={config}
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            resetToFirstPage();
          }}
          isSearching={isFetching && !isLoading}
          categoryFilter={categoryFilter}
          failureTypeFilter={failureTypeFilter}
          onCategoryFilterChange={handleCategoryFilterChange}
          onFailureTypeFilterChange={handleFailureTypeFilterChange}
          onFilterClick={() => setFilterOpen(true)}
          filterOpen={filterOpen}
          filterCount={countActiveInvoiceFilters(advancedFilters)}
          insights={overviewInsights}
        />

        {config.showFilterButton && (
          <InvoiceFilterOffcanvas
            open={filterOpen}
            onOpenChange={setFilterOpen}
            value={advancedFilters}
            onApply={applyFilters}
            showAssignee={variant !== "my-invoices"}
          />
        )}

        <ActiveFilterChips
          chips={activeFilterChips}
          onRemove={removeFilterChip}
          onClearAll={clearToolbar}
          clearLabel="Clear"
          clearMin={1}
        />

        <InvoiceSelectionSummary
          count={selectedCount}
          netTotal={selectedTotals.net}
          grossTotal={selectedTotals.total}
        />

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-hidden",
            selectedCount > 0 && "pb-14",
          )}
        >
          <InvoiceDataTable
            variant={variant}
            invoices={invoices}
            isLoading={isLoading}
            selectedIds={visibleSelectedIds}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            assignees={users}
            onRequestAssignees={requestAssignees}
            onAssigneeChange={handleAssigneeChange}
            onDelete={setPendingDelete}
            onChangeStatus={canUpdate ? setStatusTarget : undefined}
            onChangeCategory={canRecategorise ? setCategoryTarget : undefined}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            deletingId={isDeleting ? pendingDelete?.id : null}
            caption={config.title}
          />
        </div>

        <InvoiceSelectionBar
          count={selectedCount}
          onClear={() => setSelectedIds(new Set())}
          assignees={users}
          onRequestAssignees={requestAssignees}
          canChangeStatus={canUpdate}
          canRecategorise={canRecategorise}
          canDelete={canDelete}
          onBulkAssign={handleBulkAssign}
          onBulkDelete={() => setBulkDeleteOpen(true)}
          onBulkDownload={canDownload ? handleBulkDownload : undefined}
          onBulkStatus={() => setBulkStatusOpen(true)}
          onBulkCategory={() => setBulkCategoryOpen(true)}
          isWorking={isBulkWorking}
          progress={bulkProgress}
        />

        <div className="page-footer">
          <label className="flex items-center gap-2 text-sm">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                resetToFirstPage();
              }}
              className="num h-8 rounded-lg border-0 bg-muted px-2 text-sm text-foreground"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav aria-label="Pagination" className="flex items-center gap-1">
            <span className="num me-2 text-sm" aria-live="polite">
              {formatInvoiceCount(pagination.total)}
            </span>
            {pagination.total > 0 && (
              <PageJump
                key={currentPage}
                page={currentPage}
                totalPages={totalPages}
                onJump={setPageNumber}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon hidden bg-muted shadow-none sm:inline-flex"
              disabled={currentPage <= 1 || isFetching}
              onClick={() => setPageNumber(1)}
              aria-label="First page"
            >
              <ChevronsLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon bg-muted shadow-none"
              disabled={currentPage <= 1 || isFetching}
              onClick={() => setPageNumber(currentPage - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon bg-muted shadow-none"
              disabled={currentPage >= totalPages || isFetching}
              onClick={() => setPageNumber(currentPage + 1)}
              aria-label="Next page"
            >
              <ChevronRight />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon hidden bg-muted shadow-none sm:inline-flex"
              disabled={currentPage >= totalPages || isFetching}
              onClick={() => setPageNumber(totalPages)}
              aria-label="Last page"
            >
              <ChevronsRight />
            </Button>
          </nav>
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this invoice?"
        description={
          pendingDelete
            ? `${describeInvoice(pendingDelete)} will be permanently removed.`
            : undefined
        }
        confirmLabel="Delete"
        confirmVariant="destructive"
        isSubmitting={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedCount} invoice${selectedCount === 1 ? "" : "s"}?`}
        description="This permanently removes the selected invoices and cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        isSubmitting={isBulkWorking}
        onConfirm={handleBulkDelete}
      />

      <InvoiceStatusModal
        invoice={statusTarget}
        open={Boolean(statusTarget)}
        onOpenChange={(open) => !open && setStatusTarget(null)}
      />

      <InvoiceBulkStatusModal
        open={bulkStatusOpen}
        onOpenChange={setBulkStatusOpen}
        count={selectedCount}
        onSubmit={handleBulkStatus}
        isSubmitting={isBulkWorking}
      />

      <InvoiceBulkCategoryModal
        open={bulkCategoryOpen}
        onOpenChange={setBulkCategoryOpen}
        count={selectedCount}
        onSubmit={handleBulkCategory}
        isSubmitting={isBulkWorking}
      />

      <InvoiceCategoryModal
        invoice={categoryTarget}
        open={Boolean(categoryTarget)}
        onOpenChange={(open) => !open && setCategoryTarget(null)}
      />

      {/* `location.key` keeps the announcement fresh when the same queue is re-entered. */}
      <span key={location.key} className="sr-only" aria-live="polite">
        {isFetching ? "Loading invoices" : `${pagination.total} invoices loaded`}
      </span>
    </div>
  );
}
